// Onboarding -- iteration 187 (Slim Parent Onboarding).
// Was a blocking 4-step preference wizard (073). Per user direction the
// preferences ("set them later") move out of the first-run gate: they already
// live, non-blocking, on /preferences (in the nav), and POPIA/terms consent is
// already enforced globally by <ConsentGate>. So first-run is now a single
// welcome screen that completes onboarding and points the parent at adding
// their children (family code lives on /my-children). Shown once after first
// login (tracked via User.onboarding_completed_at).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, SlidersHorizontal } from "lucide-react";
import * as api from "@/lib/api";

export default function OnboardingPage() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState("");

  // Complete onboarding first, then go wherever the parent chose. We must mark
  // it complete before navigating: the RequireAuth guard bounces an
  // incomplete-onboarding user straight back to /onboarding otherwise.
  async function completeAnd(destination: string) {
    setFinishing(true);
    setError("");
    try {
      await api.completeOnboarding();
      await refreshUser();
      navigate(destination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to continue");
      setFinishing(false);
    }
  }

  return (
    <div data-page="onboarding" className="mx-auto flex min-h-screen max-w-lg flex-col justify-center p-4">
      <Card className="border-0 bg-transparent shadow-none md:border md:bg-background md:shadow-sm">
        <CardHeader className="px-0 md:px-6">
          <CardTitle data-field="welcome-title" className="text-2xl">Welcome to DugAds</CardTitle>
          <CardDescription>
            Let's get you set up. Add your children to follow their teams, events and results.
            You can set notification and follow preferences any time from Preferences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-0 md:px-6">
          <Button
            type="button"
            className="w-full justify-start gap-3"
            onClick={() => completeAnd("/my-children")}
            disabled={finishing}
            data-action="add-children"
          >
            <Heart className="h-4 w-4" aria-hidden />
            {finishing ? "Just a moment..." : "Add my children"}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={() => completeAnd("/preferences")}
            disabled={finishing}
            data-action="set-preferences"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            Set my preferences
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => completeAnd("/")}
            disabled={finishing}
            data-action="skip"
          >
            Skip for now
          </Button>

          {error && <p className="text-center text-sm text-destructive" data-error>{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
